import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { cambiarClave } from "../services/usuarioService";
import PasswordStrengthIndicator from "./PasswordStrengthIndicator";
import {
  evaluatePasswordStrength,
  PASSWORD_REQUIREMENTS_TEXT,
} from "../utils/passwordValidation";

const ChangePasswordForm = () => {
  const { user } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const toggleShowCurrent = () => setShowCurrent((prev) => !prev);
  const toggleShowNew = () => setShowNew((prev) => !prev);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      return Swal.fire("Error", "Ambos campos son requeridos", "error");
    }

    if (!evaluatePasswordStrength(newPassword).isValid) {
      return Swal.fire(
        "Contraseña insegura",
        PASSWORD_REQUIREMENTS_TEXT,
        "warning"
      );
    }

    try {
      const userId = user?.idUsuario || user?.id;
      if (!userId) {
        throw new Error("No se encontró el id del usuario autenticado");
      }

      const response = await cambiarClave(userId, {
        clave_actual: currentPassword,
        nueva_clave: newPassword,
      });

      if (response.success) {
        Swal.fire(
          "Éxito",
          response.message || "Contraseña actualizada correctamente",
          "success"
        );
        setCurrentPassword("");
        setNewPassword("");
      } else {
        const errorMessage =
          response?.messages?.error || "Error al actualizar la contraseña";
        Swal.fire("Error", errorMessage, "error");
      }
    } catch (error) {
      console.error(error);
      const serverMessage = error?.response?.data?.message || error?.response?.data?.messages?.error;
      Swal.fire(
        "Error",
        serverMessage || error?.message || "Error en la conexión con el servidor",
        "error"
      );
    }
  };

  return (
    <Card sx={{ margin: 1, borderRadius: 2, boxShadow: "none", bgcolor: "#fff" }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <LockIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Cambiar contraseña</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Contraseña actual"
              type={showCurrent ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowCurrent} edge="end">
                      {showCurrent ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nueva contraseña"
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={
                newPassword !== "" &&
                !evaluatePasswordStrength(newPassword).isValid
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowNew} edge="end">
                      {showNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={PASSWORD_REQUIREMENTS_TEXT}
            />
            <PasswordStrengthIndicator password={newPassword} />
          </Grid>
        </Grid>

        <Box mt={3} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangePassword}
            disabled={
              !currentPassword ||
              !newPassword ||
              !evaluatePasswordStrength(newPassword).isValid
            }
          >
            Actualizar contraseña
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordForm;
